import styled from 'styled-components';
import { colors, r8, s16 } from '@ardoq/design-tokens';
import { BigButton } from '@ardoq/snowflakes';
import {
  AddChartIcon,
  AddHeaderIcon,
  AddIcon,
  AddNoteIcon,
  Icon,
  IconName,
} from '@ardoq/icons';
import { PlainTextPopover, WithUncontrolledPopover } from '@ardoq/popovers';
import { WidgetTypes } from '@ardoq/api-types';
import { DashboardBuilderWidgetsProps } from './widgets/types';
import { AddWidgetButtonsProps, WidgetsCommands } from './types';
import { getLayoutForNewWidget } from './layoutUtils';
import { type WithI18nLocale, t } from '@ardoq/i18n';

const AddNewChartButtonAnchor = styled.div`
  display: flex;

  > button {
    height: 100%;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${s16};
  border-radius: ${r8};
  outline: 1px solid ${colors.borderAction};
  height: 100%;

  > button,
  > ${AddNewChartButtonAnchor} {
    height: calc(30% - 32px);
    font-size: 0.9rem;
  }

  > ${AddNewChartButtonAnchor} button {
    font-size: 0.9rem;
  }
`;
export const getAddWidgetButtonProps = (
  widgets: DashboardBuilderWidgetsProps[],
  widgetsCommands: WidgetsCommands,
  isCreateNewChartButtonEnabled: boolean,
  showPowerBiIframeWidget: boolean
): AddWidgetButtonsProps => {
  const addWidget = (viewType: WidgetTypes) => {
    widgetsCommands.createNewWidget(
      getLayoutForNewWidget(widgets, viewType),
      viewType
    );
  };
  return {
    createNewChartWidget: () => addWidget(WidgetTypes.BAR_CHART),
    createNewHeaderWidget: () => addWidget(WidgetTypes.HEADER),
    createNewNoteWidget: () => addWidget(WidgetTypes.NOTE),
    createNewPowerBiIframeWidget: () => addWidget(WidgetTypes.POWER_BI_IFRAME),
    canCreateNewChart: isCreateNewChartButtonEnabled,
    showPowerBiIframeWidget,
  };
};
const AddWidgetButtons = ({
  createNewChartWidget,
  createNewHeaderWidget,
  createNewNoteWidget,
  createNewPowerBiIframeWidget,
  canCreateNewChart = true,
  showPowerBiIframeWidget = false,
  i18nLocale,
}: AddWidgetButtonsProps & WithI18nLocale) => {
  return (
    <ButtonContainer>
      <WithUncontrolledPopover
        content={
          !canCreateNewChart ? (
            <PlainTextPopover>
              {t(
                i18nLocale,
                'dashboard:addWidget.clearFiltersToAddChart',
                'Clear dashboard filters to add new chart'
              )}
            </PlainTextPopover>
          ) : null
        }
      >
        <AddNewChartButtonAnchor>
          <BigButton
            onClick={createNewChartWidget}
            isDisabled={!canCreateNewChart}
          >
            <AddChartIcon />
            {t(i18nLocale, 'dashboard:addWidget.addNewChart', 'Add new chart')}
            <AddIcon />
          </BigButton>
        </AddNewChartButtonAnchor>
      </WithUncontrolledPopover>
      <BigButton onClick={createNewHeaderWidget}>
        <AddHeaderIcon />
        {t(i18nLocale, 'dashboard:addWidget.addNewHeading', 'Add new heading')}
        <AddIcon />
      </BigButton>
      <BigButton onClick={createNewNoteWidget}>
        <AddNoteIcon />
        {t(i18nLocale, 'dashboard:addWidget.addNote', 'Add note')}
        <AddIcon />
      </BigButton>
      {showPowerBiIframeWidget && (
        <BigButton onClick={createNewPowerBiIframeWidget}>
          <Icon iconName={IconName.POWER_BI} />
          {t(i18nLocale, 'dashboard:addWidget.addPowerBI', 'Add PowerBI embed')}
          <AddIcon />
        </BigButton>
      )}
    </ButtonContainer>
  );
};

export { AddWidgetButtons };
